import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getProjectConversationsWithLatestNotes } from '../../Requests/ConversationRequests';
import { ChatBubbleLeftRightIcon, UserIcon } from '@heroicons/react/24/outline';
import UpgradedLoader from '../UpgradedLoader';

function ConversationsWidget({ project, preloadedData = null, showProjectBadge = false }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [conversations, setConversations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showingUnread, setShowingUnread] = useState(true);

    // Move useCallback to component level (not inside useEffect)
    const processConversationsData = useCallback((conversationsData) => {
        // Show unread conversations first, fallback to recent conversations with notes
        let conversationsToShow = conversationsData
            .filter(conv => conv.latest_note && conv.unread_count > 0) // Only show conversations with unread notes
            .sort((a, b) => new Date(b.latest_note.created_at) - new Date(a.latest_note.created_at))
            .slice(0, 5); // Show up to 5 unread conversations

        // If no unread conversations, show recent conversations with notes
        if (conversationsToShow.length === 0) {
            conversationsToShow = conversationsData
                .filter(conv => conv.latest_note) // Show conversations that have notes
                .sort((a, b) => new Date(b.latest_note.created_at) - new Date(a.latest_note.created_at))
                .slice(0, 5); // Show up to 5 recent conversations
            setShowingUnread(false);
        } else {
            setShowingUnread(true);
        }
        
        setConversations(conversationsToShow);
    }, []); // setState functions are stable, no deps needed

    useEffect(() => {
        if (!project?.uuid && !preloadedData) return;

        // Use preloaded data if available (performance optimization)
        if (preloadedData) {
            try {
                setLoading(true);
                setError(null);
                processConversationsData(preloadedData);
            } catch (err) {
                console.error('Error processing preloaded conversations:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        } else {
            // Fallback to API call if no preloaded data
            const fetchConversations = async () => {
                try {
                    setLoading(true);
                    setError(null);
                    
                    const response = await getProjectConversationsWithLatestNotes(project.uuid);
                    const conversationsData = response.data?.data || [];
                    processConversationsData(conversationsData);
                } catch (err) {
                    console.error('Error fetching conversations:', err);
                    setError(err.message);
                } finally {
                    setLoading(false);
                }
            };

            fetchConversations();
        }
    }, [project?.uuid, preloadedData?.length]); // Use length to prevent object reference issues

    // Format time ago
    const formatTimeAgo = (dateString) => {
        const now = new Date();
        const date = new Date(dateString);
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffMins < 1) return t('Conversations.justNow');
        if (diffMins < 60) return `${diffMins}m`;
        if (diffHours < 24) return `${diffHours}h`;
        if (diffDays < 7) return `${diffDays}d`;
        
        return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    };

    // Truncate text
    const truncateText = (text, maxLength = 80) => {
        if (!text || text.length <= maxLength) return text;
        return text.substring(0, maxLength).trim() + '...';
    };

    const handleConversationClick = (conversation) => {
        const projectUuid = project?.uuid ?? conversation.project?.uuid;
        if (projectUuid) {
            navigate(`/project/${projectUuid}/conversations`, {
                state: { selectedConversationId: conversation.uuid }
            });
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <UpgradedLoader className="animate-spin" w={6} h={6} bg="bg-iavisus-100" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-center">
                    <p className="text-red-500 text-sm">Error loading conversations</p>
                </div>
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col">
            {/* Header */}
            <div className="flex items-center p-4 border-b border-gray-200">
                <ChatBubbleLeftRightIcon className="h-5 w-5 text-iavisus-100 mr-2" />
                <h3 className="text-sm font-medium text-gray-900">
                    {showingUnread ? t('Conversations.unreadConversations') : t('Conversations.recentConversations')}
                </h3>
            </div>

            {/* Conversations List */}
            <div className="flex-1 overflow-y-auto">
                {conversations.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center p-4">
                        <ChatBubbleLeftRightIcon className="h-8 w-8 text-gray-400 mb-2" />
                        <p className="text-sm text-gray-500 mb-1">
                            {showingUnread ? t('Conversations.noUnreadConversations') : t('Conversations.noConversations')}
                        </p>
                        <p className="text-xs text-gray-400">
                            {showingUnread ? t('Conversations.allCaughtUp') : t('Conversations.startFirstConversation')}
                        </p>
                    </div>
                ) : (
                    <div className="divide-y divide-gray-100">
                        {conversations.map((conversation) => (
                            <div
                                key={conversation.uuid}
                                data-testid={`conversation-item-${conversation.uuid}`}
                                onClick={() => handleConversationClick(conversation)}
                                className="p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                            >
                                <div className="space-y-2">
                                    {/* Conversation title */}
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-1.5 min-w-0 pr-2">
                                            <h4 className="text-sm font-medium text-gray-900 truncate">
                                                {conversation.title}
                                            </h4>
                                            {showProjectBadge && conversation.project?.name && (
                                                <span data-testid="conversation-project-badge" className="shrink-0 inline-block text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full truncate max-w-[100px]">
                                                    {conversation.project.name}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center text-xs text-gray-400 shrink-0">
                                            {conversation.unread_count > 0 && (
                                                <span className="inline-flex items-center justify-center w-4 h-4 bg-iavisus-100 text-white text-xs font-bold rounded-full mr-1">
                                                    {conversation.unread_count > 9 ? '9+' : conversation.unread_count}
                                                </span>
                                            )}
                                            <span>{formatTimeAgo(conversation.latest_note?.created_at)}</span>
                                        </div>
                                    </div>

                                    {/* Latest note */}
                                    {conversation.latest_note && (
                                        <div className="flex items-start space-x-2">
                                            <div className="flex items-center text-xs text-gray-500 shrink-0">
                                                <UserIcon className="h-3 w-3 mr-1" />
                                                <span className="max-w-[60px] truncate">
                                                    {conversation.latest_note.user?.name || 'Unknown'}
                                                </span>:
                                            </div>
                                            <p className="text-xs text-gray-600 line-clamp-2 flex-1">
                                                {truncateText(conversation.latest_note.content, 60)}
                                            </p>
                                        </div>
                                    )}

                                    {/* Notes count */}
                                    <div className="flex justify-between items-center">
                                        <span className="text-xs text-gray-400">
                                            {conversation.notes_count || 0} {(conversation.notes_count || 0) === 1 ? t('Conversations.note') : t('Conversations.notes')}
                                        </span>
                                        {!conversation.latest_note?.is_read && conversation.latest_note?.user?.uuid !== 'current-user' && (
                                            <div className="h-2 w-2 bg-iavisus-100 rounded-full"></div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

// Memoize component to prevent unnecessary re-renders
export default React.memo(ConversationsWidget, (prevProps, nextProps) => {
    return prevProps.project?.uuid === nextProps.project?.uuid &&
           prevProps.preloadedData === nextProps.preloadedData &&
           prevProps.showProjectBadge === nextProps.showProjectBadge;
});